import React, { useMemo } from "react";
import { Flex, Tag, Typography } from "antd";
import {
  CloseCircleOutlined,
  MailOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { useTranslate } from "@refinedev/core";
import { displayEmail } from "@/utilities";
import { AffiliationIcon } from "./AffiliationIcon";
import { fetchOperatorRows } from "./people-filter.api";
import {
  affiliationKey,
  mergeOperatorCards,
  type OperatorAffiliationSelection,
  type OperatorBadge,
  type OperatorCard,
} from "./people-filter.types";
import {
  PEOPLE_FILTER_GREEN,
  PEOPLE_FILTER_GREEN_BG,
  PeopleFilterShell,
} from "./PeopleFilterShell";

const { Text } = Typography;

export interface OperatorsFilterProps {
  value?: OperatorAffiliationSelection[];
  onChange?: (value: OperatorAffiliationSelection[]) => void;
  /** Los stores elegidos en el dropdown general de la tabla. Vacío = todo lo supervisado. */
  storeCodes?: string[];
  disabled?: boolean;
  placeholder?: string;
  /** Contactos/Presupuestos/Pedidos quieren los chips dentro del buscador; el dashboard, no. */
  chipsInline?: boolean;
}

/**
 * Filtro de **Operadores**: tarjeta por persona con un badge clickeable por cada afiliación a
 * store o seller. Lo que se selecciona es la afiliación, no la persona (spec de Contactos,
 * "Filtrar por una afiliación puntual, no por la persona entera"). Un chip por afiliación.
 */
export const OperatorsFilter: React.FC<OperatorsFilterProps> = ({
  value = [],
  onChange,
  storeCodes = [],
  disabled,
  placeholder,
  chipsInline = true,
}) => {
  const t = useTranslate();
  const selectedKeys = useMemo(
    () => new Set(value.map(affiliationKey)),
    [value],
  );

  const toggle = (card: OperatorCard, badge: OperatorBadge) => {
    const key = affiliationKey(badge);
    if (selectedKeys.has(key)) {
      onChange?.(value.filter((v) => affiliationKey(v) !== key));
    } else {
      onChange?.([...value, { ...badge, personName: card.name }]);
    }
  };

  const remove = (sel: OperatorAffiliationSelection) =>
    onChange?.(value.filter((v) => affiliationKey(v) !== affiliationKey(sel)));

  const renderRows = (cards: OperatorCard[]) =>
    cards.map((card) => (
      <div
        key={card.userId}
        style={{ padding: "10px 12px", borderBottom: "1px solid #f5f5f5" }}
      >
        <Text strong style={{ fontSize: 14, display: "block" }}>
          {card.name}
        </Text>
        <Flex gap={6} align="center" style={{ marginTop: 2 }}>
          <MailOutlined style={{ fontSize: 11, color: "rgba(0,0,0,0.35)" }} />
          <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
            {displayEmail(card.email) || "–"}
          </Text>
        </Flex>
        {card.telefono && (
          <Flex gap={6} align="center">
            <WhatsAppOutlined
              style={{ fontSize: 11, color: "rgba(0,0,0,0.35)" }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {card.telefono}
            </Text>
          </Flex>
        )}
        <Flex wrap gap={6} style={{ marginTop: 8 }}>
          {card.badges.map((badge) => {
            const active = selectedKeys.has(affiliationKey(badge));
            const color = active ? PEOPLE_FILTER_GREEN : "rgba(0,0,0,0.65)";
            return (
              <span
                key={affiliationKey(badge)}
                role="checkbox"
                aria-checked={active}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(card, badge);
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontSize: 12,
                  lineHeight: "18px",
                  cursor: "pointer",
                  userSelect: "none",
                  color,
                  fontWeight: active ? 600 : 400,
                  border: `1px solid ${
                    active ? PEOPLE_FILTER_GREEN : "#d9d9d9"
                  }`,
                  background: active ? PEOPLE_FILTER_GREEN_BG : "#fff",
                }}
              >
                <AffiliationIcon type={badge.type} color={color} />
                {badge.label}
              </span>
            );
          })}
        </Flex>
      </div>
    ));

  const chips = value.length ? (
    <>
      {value.map((sel) => (
        <Tag
          key={affiliationKey(sel)}
          closable
          closeIcon={<CloseCircleOutlined />}
          onClose={(e) => {
            e.preventDefault();
            remove(sel);
          }}
          style={{
            margin: 0,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            color: PEOPLE_FILTER_GREEN,
            background: PEOPLE_FILTER_GREEN_BG,
            border: `1px solid ${PEOPLE_FILTER_GREEN}`,
          }}
        >
          <AffiliationIcon type={sel.type} color={PEOPLE_FILTER_GREEN} />
          {sel.label} : {sel.personName}
        </Tag>
      ))}
    </>
  ) : null;

  return (
    <PeopleFilterShell<OperatorCard>
      placeholder={placeholder ?? t("filter.operators.placeholder")}
      searchPlaceholder={t("filter.people.search")}
      emptyText={t("filter.operators.empty")}
      noResultsText={(q) => t("filter.people.noResults", { q })}
      selectedCount={value.length}
      selectedSummary={t("filter.people.selected", { count: value.length })}
      disabled={disabled}
      fetchKey={storeCodes.join(",")}
      fetchRows={async (search, signal) =>
        mergeOperatorCards(
          await fetchOperatorRows({ storeCodes, search, signal }),
        )
      }
      renderRows={renderRows}
      chips={chips}
      chipsInline={chipsInline}
    />
  );
};
